/**
 * ToolWindow - Unified floating window component
 *
 * Combines features from:
 * - DraggablePanel (drag, resize, persist position)
 * - WindowFrame (title bar, styling)
 * - Layout System (collapse/expand)
 *
 * Use this for ALL floating tool windows (AI tools, entity palettes, etc.)
 *
 * Example:
 * ```tsx
 * <ToolWindow
 *   id="ai-assistant"
 *   title="AI Assistant"
 *   icon="🤖"
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   defaultPosition={{ x: 100, y: 100 }}
 *   defaultSize={{ width: 400, height: 600 }}
 *   collapsible={true}
 *   resizable={true}
 *   minimizable={true}
 * >
 *   <AIChatContent />
 * </ToolWindow>
 * ```
 */

import React, {
  forwardRef,
  useImperativeHandle,
  useState,
} from 'react';
import { playerWindowGeometry } from './playerWindowGeometry';
import {
  useWindowGeometry,
  type ToolWindowHandle,
  type ToolWindowState,
} from './useWindowGeometry';
import './ToolWindow.css';

export type { ToolWindowState, ToolWindowHandle };

export interface ToolWindowProps {
  /** Unique ID for persisting position/size */
  id: string;
  /** Window title shown in header */
  title: string;
  /** Icon shown next to title */
  icon?: string;
  /** Control visibility */
  isOpen: boolean;
  /** Called when window is closed (X button) */
  onClose: () => void;
  children: React.ReactNode;
  /** Initial position (defaults to center) */
  defaultPosition?: { x: number; y: number };
  /** Initial size (defaults to 400x500) */
  defaultSize?: { width: number; height: number };
  /** Minimum window size */
  minSize?: { width: number; height: number };
  /** Maximum window size */
  maxSize?: { width: number; height: number };
  /** Allow collapse to title bar */
  collapsible?: boolean;
  /** Allow resize */
  resizable?: boolean;
  /** Allow minimize to corner */
  minimizable?: boolean;
  /** Optional manual zIndex override for canvas stacking */
  zIndex?: number;
  /** Show on top when dragging */
  bringToFrontOnDrag?: boolean;
  /** Custom class name */
  className?: string;
  /** Called when position changes */
  onPositionChange?: (x: number, y: number) => void;
  /** Called when size changes */
  onSizeChange?: (width: number, height: number) => void;
  /** Called when collapsed state changes */
  onCollapsedChange?: (collapsed: boolean) => void;
  /** Disable localStorage geometry for ephemeral diagnostic windows. */
  persistState?: boolean;
  /** Launch expanded even when the saved chrome state was collapsed/minimized. */
  restoreOnOpen?: boolean;
  /** Stable selector for VQA and host integrations. */
  testId?: string;
}

const DEFAULT_MIN_SIZE = { width: 200, height: 100 };
const DEFAULT_MAX_SIZE = { width: 1200, height: 900 };

export const ToolWindow = forwardRef<ToolWindowHandle, ToolWindowProps>(({
  id,
  title,
  icon,
  isOpen,
  onClose,
  children,
  defaultPosition,
  defaultSize = { width: 400, height: 500 },
  minSize = DEFAULT_MIN_SIZE,
  maxSize = DEFAULT_MAX_SIZE,
  collapsible = true,
  resizable = true,
  minimizable = true,
  zIndex: initialZIndex = 100,
  bringToFrontOnDrag = true,
  className = '',
  onPositionChange,
  onSizeChange,
  onCollapsedChange,
  persistState = true,
  restoreOnOpen = false,
  testId,
}, forwardedRef) => {
  const isPlayer = className.split(' ').includes('toolwindow--player');
  const {
    state,
    isDragging,
    zIndex,
    windowRef,
    startDrag,
    startResize,
    toggleCollapse,
    toggleMinimize,
    handle,
  } = useWindowGeometry({
    id,
    defaultPosition,
    defaultSize,
    minSize,
    maxSize,
    persistState,
    restoreOnOpen,
    isPlayerWindow: isPlayer,
    bringToFrontOnDrag,
    initialZIndex,
    onPositionChange,
    onSizeChange,
    onCollapsedChange,
  });

  useImperativeHandle(forwardedRef, () => handle, [handle]);

  if (!isOpen) return null;

  // Minimized state - just show title bar
  if (state.minimized && minimizable) {
    return (
      <div
        ref={windowRef}
        data-testid={testId}
        className={`toolwindow toolwindow-minimized ${className}`}
        style={{
          position: 'absolute',
          left: state.x,
          top: state.y,
          zIndex,
          cursor: 'grab',
          pointerEvents: 'auto', // Ensure window receives clicks
        }}
        onMouseDown={startDrag}
      >
        <div className="toolwindow-header">
          <div className="toolwindow-title">
            {icon && <span className="toolwindow-icon">{icon}</span>}
            <span>{title}</span>
          </div>
          <div className="toolwindow-controls">
            <button
              className="toolwindow-button"
              onClick={toggleMinimize}
              title="Restore"
            >
              □
            </button>
            <button
              className="toolwindow-button toolwindow-close"
              onClick={onClose}
              title="Close"
            >
              ×
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={windowRef}
      data-testid={testId}
      className={`toolwindow ${isDragging ? 'toolwindow-dragging' : ''} ${className}`}
      style={{
        position: 'absolute',
        left: state.x,
        top: state.y,
        width: state.width,
        height: state.collapsed ? 'auto' : state.height,
        ...(className.split(' ').includes('toolwindow--player') ? playerWindowGeometry(state, minSize) : {}),
        zIndex,
        pointerEvents: 'auto', // Ensure window receives clicks
      }}
    >
      {/* Header - draggable */}
      <div
        className="toolwindow-header"
        onMouseDown={startDrag}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div className="toolwindow-title">
          {icon && <span className="toolwindow-icon">{icon}</span>}
          <span>{title}</span>
        </div>
        <div className="toolwindow-controls">
          {collapsible && (
            <button
              className="toolwindow-button"
              onClick={toggleCollapse}
              title={state.collapsed ? 'Expand' : 'Collapse'}
            >
              {state.collapsed ? '▼' : '▲'}
            </button>
          )}
          {minimizable && (
            <button
              className="toolwindow-button"
              onClick={toggleMinimize}
              title="Minimize"
            >
              −
            </button>
          )}
          <button
            className="toolwindow-button toolwindow-close"
            onClick={onClose}
            title="Close"
          >
            ×
          </button>
        </div>
      </div>

      {/* Content */}
      {!state.collapsed && (
        <div className="toolwindow-content">
          {children}
        </div>
      )}

      {/* Resize Handle */}
      {resizable && !state.collapsed && (
        <div
          className="toolwindow-resize-handle"
          onMouseDown={startResize}
          title="Drag to resize"
        />
      )}
    </div>
  );
});

ToolWindow.displayName = 'ToolWindow';

/**
 * ToolWindowManager - Manages multiple tool windows
 *
 * Ensures only one window is on top when clicked.
 */
interface ToolWindowManagerState {
  [id: string]: number; // z-index for each window
}

export const useToolWindowManager = () => {
  const [windowOrder, setWindowOrder] = useState<ToolWindowManagerState>({});
  const baseZIndex = 100;
  const topZIndex = 1000;

  const bringToFront = (id: string) => {
    setWindowOrder((prev: ToolWindowManagerState) => {
      const values = Object.values(prev) as number[];
      const currentMax = values.length > 0 ? Math.max(...values, baseZIndex) : baseZIndex;
      return { ...prev, [id]: Math.min(currentMax + 1, topZIndex) };
    });
    return windowOrder[id] || baseZIndex;
  };

  const getZIndex = (id: string) => {
    return windowOrder[id] || baseZIndex;
  };

  return { bringToFront, getZIndex };
};
